import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Calendar, User, Copy, ExternalLink, Edit2, Share2, Trash2, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface ContextItemDetailProps {
  item: {
    name: string;
    path: string;
    content: string;
    lastModified: string;
    modifiedBy: string;
    usedBy: string[];
    referencedIn: string[];
    tags: string[];
  };
  onClose: () => void;
}
export function ContextItemDetail({
  item,
  onClose
}: ContextItemDetailProps) {
  const [viewMode, setViewMode] = useState<'preview' | 'raw'>('preview');
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} className="fixed inset-0 z-50 flex items-center justify-center p-8 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-5xl h-[85vh] bg-surface-900 border border-surface-600 rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-surface-600 bg-surface-900 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center border border-primary-500/20">
              <FileText className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs text-text-muted mb-1">
                <span>{item.path}</span>
              </div>
              <h2 className="text-lg font-semibold text-text-primary">{item.name}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm">
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="secondary" size="sm">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button variant="ghost" size="sm" className="text-danger-400 hover:text-danger-300 hover:bg-danger-500/10">
              <Trash2 className="w-4 h-4" />
            </Button>
            <div className="w-px h-6 bg-surface-800 mx-2" />
            <button onClick={onClose} className="text-text-muted hover:text-text-primary">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Main Content */}
          <div className="flex-1 flex flex-col border-r border-surface-600">
            <div className="p-2 border-b border-surface-600 flex items-center justify-between bg-surface-900/50">
              <div className="flex gap-1 bg-surface-800 p-1 rounded-lg">
                <button onClick={() => setViewMode('preview')} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${viewMode === 'preview' ? 'bg-surface-700 text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>
                  Preview
                </button>
                <button onClick={() => setViewMode('raw')} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${viewMode === 'raw' ? 'bg-surface-700 text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>
                  Raw
                </button>
              </div>
              <Button variant="ghost" size="sm" className="text-xs">
                <Copy className="w-3 h-3 mr-2" />
                Copy content
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-[#0B0F17]">
              {viewMode === 'preview' ? <div className="prose prose-invert max-w-none">
                  <h1>{item.name.replace('.md', '')}</h1>
                  <p className="text-text-primary leading-relaxed">
                    {item.content}
                  </p>
                  <h3>Key Concepts</h3>
                  <ul className="text-text-primary">
                    <li>Concept 1: Implementation details</li>
                    <li>Concept 2: Architecture overview</li>
                    <li>Concept 3: Performance considerations</li>
                  </ul>
                  <pre className="bg-surface-800 p-4 rounded-lg overflow-x-auto">
                    <code className="text-sm text-primary-300">
                      {`function init() {
  axiom.info("Initializing context...");
  return true;
}`}
                    </code>
                  </pre>
                </div> : <pre className="font-mono text-sm text-text-primary whitespace-pre-wrap">
                  {item.content}
                </pre>}
            </div>
          </div>

          {/* Metadata Sidebar */}
          <div className="w-80 bg-surface-900 p-6 overflow-y-auto">
            <div className="space-y-6">
              <div>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                  Details
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Modified
                    </span>
                    <span className="text-text-primary">{item.lastModified}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary flex items-center gap-2">
                      <User className="w-4 h-4" /> Author
                    </span>
                    <span className="text-text-primary">{item.modifiedBy}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                  Used By
                </h3>
                <div className="space-y-2">
                  {item.usedBy.map((task, i) => <div key={i} className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 cursor-pointer">
                      <ExternalLink className="w-3 h-3" />
                      {task}
                    </div>)}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                  Referenced In
                </h3>
                <div className="space-y-3">
                  {item.referencedIn.map((ref, i) => <div key={i} className="p-3 rounded-lg bg-surface-800/50 border border-surface-600 text-xs">
                      <p className="text-text-primary mb-1">
                        "...as mentioned in {item.name}..."
                      </p>
                      <span className="text-text-muted block text-[10px]">
                        {ref}
                      </span>
                    </div>)}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {item.tags.map((tag, i) => <Badge key={i} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>)}
                  <button className="text-xs text-text-muted hover:text-text-primary px-2 py-1 rounded border border-dashed border-surface-500 hover:border-surface-400">
                    + Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}